import type { AllocationLineForm } from "./transaction-utils";

const COST_TYPES = [
  { value: "program_direct", label: "Program / Direct" },
  { value: "administration", label: "Administration" },
  { value: "shared_common", label: "Shared / Common" },
] as const;

interface AllocationLinesEditorProps {
  lines: AllocationLineForm[];
  onChange: (lines: AllocationLineForm[]) => void;
  units: { id: string; name: string; code: string }[];
  funds: { id: string; name: string }[];
  categories: { id: string; name: string }[];
  showCostType?: boolean;
}

export function AllocationLinesEditor({
  lines,
  onChange,
  units,
  funds,
  categories,
  showCostType = false,
}: AllocationLinesEditorProps) {
  function updateLine(index: number, patch: Partial<AllocationLineForm>) {
    onChange(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  function addLine() {
    onChange([
      ...lines,
      {
        unitId: units[0]?.id ?? "",
        fundId: funds[0]?.id ?? "",
        categoryId: categories[0]?.id ?? "",
        amount: "",
        costType: "program_direct",
      },
    ]);
  }

  function removeLine(index: number) {
    if (lines.length <= 1) {
      return;
    }
    onChange(lines.filter((_, i) => i !== index));
  }

  return (
    <div className="grid gap-3">
      {lines.map((line, index) => (
        <div
          key={index}
          className="grid gap-3 rounded-xl border border-edlab-line bg-edlab-bg/40 p-3"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold tracking-[0.08em] text-edlab-muted uppercase">
              Allocation {index + 1}
            </span>
            {index > 0 ? (
              <button
                type="button"
                className="rounded-md border border-edlab-line px-2 py-1 text-xs text-edlab-muted hover:bg-white"
                aria-label="Remove allocation"
                onClick={() => {
                  removeLine(index);
                }}
              >
                Remove
              </button>
            ) : null}
          </div>
          <div className="form-grid">
            <label className="form-field">
              Programme / unit
              <select
                className="edlab-select"
                value={line.unitId}
                onChange={(e) => {
                  updateLine(index, { unitId: e.target.value });
                }}
              >
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              Funding source
              <select
                className="edlab-select"
                value={line.fundId}
                onChange={(e) => {
                  updateLine(index, { fundId: e.target.value });
                }}
              >
                {funds.map((fund) => (
                  <option key={fund.id} value={fund.id}>
                    {fund.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              Account / category
              <select
                className="edlab-select"
                value={line.categoryId}
                onChange={(e) => {
                  updateLine(index, { categoryId: e.target.value });
                }}
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              Amount · PKR
              <input
                type="number"
                min="0.01"
                step="0.01"
                className="edlab-input"
                value={line.amount}
                placeholder="0.00"
                onChange={(e) => {
                  updateLine(index, { amount: e.target.value });
                }}
              />
            </label>
            {showCostType ? (
              <label className="form-field">
                Cost type
                <select
                  className="edlab-select"
                  value={line.costType ?? "program_direct"}
                  onChange={(e) => {
                    updateLine(index, { costType: e.target.value });
                  }}
                >
                  {COST_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        </div>
      ))}
      <button
        type="button"
        className="text-left text-sm font-medium text-edlab-green hover:underline"
        onClick={addLine}
      >
        + Add allocation line
      </button>
    </div>
  );
}
